import React, { useState } from "react";
import PropTypes from "prop-types";
import { Icons, IconButton, ListItemText, Menu, MenuItem } from "@curves/ui-base";
import { isFunction } from "@util/utils";

/**
 * OverflowMenu
 *
 * 總覽列表中每列的「更多操作」選單（通常用於 OverviewListTable 的 headerRow）。
 * 顯示為一個垂直三點圖示，點擊後展開操作選單。
 *
 * @component
 *
 * @param {Object} props
 * @param {boolean} [props.enabled=true] 是否啟用選單（false 則不顯示）
 * @param {Array<Object>} [props.menuData=[]] 選單項目陣列，每個項目會渲染成一個 `<MenuItem>`，物件格式應包含 `text` 欄位
 * @param {Function} [props.onClickActionButton] 點擊 action button 時的 callback，會傳入原始事件物件
 * @param {Function} [props.onCloseMenu] 選單關閉時的 callback，會傳入原始事件物件
 * @param {Object} [props.actionButtonProps={}] 傳遞給 `<IconButton>` 的額外 props，可用來控制樣式或 aria 屬性
 * @param {Object} [props.actionMenuProps={}] 傳遞給 `<Menu>` 的額外 props，可用來控制定位或樣式
 *
 * @example
 * <OverflowMenu
 *   enabled={true}
 *   menuData={[{ text: '編輯', onClick: handleEdit }, { text: '刪除', onClick: handleDelete }]}
 *   onClickActionButton={(e) => console.log('展開選單')}
 *   onCloseMenu={(e) => console.log('選單關閉')}
 * />
 */
const OverflowMenu = ({ enabled = true, menuData = [], onClickActionButton, onCloseMenu, actionButtonProps = {}, actionMenuProps = {} }) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	const handleCloseMenu = e => {
		setAnchorEl(null);
		isFunction(onCloseMenu) && onCloseMenu(e);
	};

	const handleClickActionButton = e => {
		setAnchorEl(e.currentTarget);
		isFunction(onClickActionButton) && onClickActionButton(e);
	};

	// 為啟用與沒有操作項就不顯示
	if (!enabled || menuData.length < 1) return null;

	return (
		<React.Fragment>
			<IconButton {...actionButtonProps} onClick={handleClickActionButton}>
				<Icons.MoreVertical />
			</IconButton>
			<Menu {...actionMenuProps} anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
				{menuData.map(({ text, onClick, ...others }) => {
					return (
						<MenuItem
							{...others}
							key={`more_label_${text}`}
							onClick={e => {
								isFunction(onClick) && onClick(e);
								handleCloseMenu(e); // 關閉選單
							}}
						>
							<ListItemText>{text}</ListItemText>
						</MenuItem>
					);
				})}
			</Menu>
		</React.Fragment>
	);
};

OverflowMenu.propTypes = {
	enabled: PropTypes.bool,
	menuData: PropTypes.array,
	onClickActionButton: PropTypes.func,
	onCloseMenu: PropTypes.func,
	actionButtonProps: PropTypes.object,
	actionMenuProps: PropTypes.object,
};

export default OverflowMenu;
